// frontend/src/hooks/useNotifications.js

import { useState, useCallback, useRef } from 'react';
import { notificationsAPI } from '../api/client';

export const useNotifications = () => {
  const [notifications, setNotifications]   = useState([]);
  const [unreadCount,   setUnreadCount]     = useState(0);
  const [loading,       setLoading]         = useState(false);
  const pollingRef = useRef(null);

  // ── Fetch unread count (lightweight — used for badge) ──────────────────────
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationsAPI.getUnreadCount();
      if (res.data.success) setUnreadCount(res.data.unreadCount ?? res.data.count ?? 0);
    } catch (err) {
      // silent — badge is non-critical
      console.warn('Unread count fetch failed:', err.message);
    }
  }, []);

  // ── Fetch full notification list (used when panel opens) ───────────────────
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await notificationsAPI.getAll({ limit: 50 });
      if (res.data.success) {
        setNotifications(res.data.data ?? []);
        // Sync badge from actual data
        setUnreadCount(
          (res.data.data ?? []).filter(n => !n.is_read).length
        );
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Mark single notification as read ──────────────────────────────────────
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationsAPI.markRead(notificationId);
      setNotifications(prev =>
        prev.map(n =>
          n.notification_id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('markAsRead failed:', err.message);
    }
  }, []);

  // ── Mark all as read ───────────────────────────────────────────────────────
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('markAllAsRead failed:', err.message);
    }
  }, []);

  // ── Delete a notification ──────────────────────────────────────────────────
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationsAPI.deleteNotification(notificationId);
      setNotifications(prev => {
        const removed = prev.find(n => n.notification_id === notificationId);
        if (removed && !removed.is_read) {
          setUnreadCount(c => Math.max(0, c - 1));
        }
        return prev.filter(n => n.notification_id !== notificationId);
      });
    } catch (err) {
      console.error('deleteNotification failed:', err.message);
    }
  }, []);

  // ── Manual refresh ─────────────────────────────────────────────────────────
  const refresh = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // ── Start / stop polling for badge count ───────────────────────────────────
  const startPolling = useCallback((intervalMs = 60_000) => {
    fetchUnreadCount(); // immediate first call
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(fetchUnreadCount, intervalMs);
  }, [fetchUnreadCount]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
    startPolling,
    stopPolling,
  };
};